import type { NotificationOperatorRecipient } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { koreanDate } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { useRemoveNotificationOperatorRecipient } from '../model/use-remove-notification-operator-recipient';

interface OperatorRecipientRowProps {
  readonly recipient: NotificationOperatorRecipient;
}

const removeConfirmMessage = (email: string): string =>
  `운영자 수신자 ${email} 을(를) 삭제하시겠습니까?\n\n다음 운영자 알림부터 이 주소로 보내지 않습니다.`;

/** 운영자 수신자 목록의 한 행 — 주소·등록일(한국 날짜)·삭제(확인창). 삭제 뒤 설정을 다시 읽을 때까지 버튼을 잠근다. */
export const OperatorRecipientRow = ({ recipient }: OperatorRecipientRowProps): ReactElement => {
  const { removeRecipient, isPending, error } = useRemoveNotificationOperatorRecipient();

  const handleRemove = (): void => {
    if (window.confirm(removeConfirmMessage(recipient.email))) {
      void removeRecipient(recipient.id);
    }
  };

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{recipient.email}</p>
        <p className="text-xs text-muted-foreground">{`등록일 ${koreanDate(recipient.createdAt)}`}</p>
        {error ? (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {error}
          </p>
        ) : null}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive"
        aria-label={`${recipient.email} 삭제`}
        isLoading={isPending}
        onClick={handleRemove}
      >
        삭제
      </Button>
    </li>
  );
};
